import { useEffect, useState, createContext, useContext, useRef } from "react";
import { GAME_TYPES, PLAY_MODES } from "./Constants";
import { callApi, callBaseApi, generateRandomString, getInlineLoader } from "./CoreHelper";
import BrazilianCheckersBoard from "./boards/BrazilianCheckersBoard";
import { Toaster, toast } from 'react-hot-toast';
import './soldrax.css';
import { AppContext as MainAppContext } from '../App';
import { WEB_SOCKET_URL } from "./CoreHelper";
import CorePaper from "./components/CorePaper";
import CoreChatter from "./components/CoreChatter";
import SpanishCheckersBoard from "./boards/SpanishCheckersBoard";
import ArubastiniBoard from "./boards/ArubastiniBoard";
import LastcardBoard from "./boards/LastcardBoard";


export const AppContext = createContext(null);
export default function App(props) {
  const mainApp = useContext(MainAppContext);

  const [ userId, setUserId ] = useState(props.userId)
  const [ mode, setMode ] = useState(props.mode);
  const [ showChat, setShowChat ] = useState(false);
  const [ userToken, setUserToken ] = useState(props.userToken)
  const [ gameType, setGameType ] = useState(props.gameType);
  const [ gameId, setGameId ] = useState(props.gameId);
  const [ playMode, setPlayMode ] = useState(props.playMode);
  const [ ready, setReady ] = useState(false);
  const [ userData, setUserData ] = useState(null);
  const [ chatRoomId, setChatRoomId ] = useState(null);
  const [ gameData, setGameData ] = useState(null);
  const [ error, setError ] = useState("");
  const webSocketRef = useRef(null)
  const [ webSocketConnected, setWebSocketConnected ] = useState(2); //0 = not, 1=conn, 2= start
  const [ webSocketReconnectTimer, setWebSocketReconnectTimer ] = useState(null);
  const wsRandomId = generateRandomString();

  const [ chatMessage, setChatMessage ] = useState('');
  const [ currentServerTimestamp, setCurrentServerTimestamp ] = useState(null);
  
  async function init() {
    
    //real initialization begins here
    setReady(false);
    await getUserData().then(async userData => {
      await getGameData().then(async gameData => {
        if(userData && gameData) {
          setupWebSocket();
          setReady(true)
        } else {
          setError("Failed to initialize the game. Check connection to the Game Server");
          tellError("Failed to initialize the game. Check connection to the Game Server");
        }
      })
    })
  }

  function setupWebSocket() {
    //subscribe for game data updates
    webSocketRef.current = new WebSocket(WEB_SOCKET_URL);
    webSocketRef.current.addEventListener('open', async () => {
      //send subscription message
      // using delay hack
      setTimeout(async () => {
        if(webSocketRef.current.readyState === WebSocket.OPEN) {

          //subscribe for game data
          webSocketRef.current.send(JSON.stringify({
            type: 'subscribe-for-game-data',
            gameId: gameId,
            userId,
            userToken,
            sourceId: 'playGame'+wsRandomId,
          }))

          //subscribe for serverCurrentTimestamp
          webSocketRef.current.send(JSON.stringify({
            type: 'subscribe-for-server-timestamp',
            userId,
            userToken,
            sourceId: 'timestampRequest'+wsRandomId,
          }))
        }
      }, 3000);
      setWebSocketConnected(1);
    })

    webSocketRef.current.addEventListener('close', (e) => {
      if(!e.wasClean) {
        setWebSocketConnected(0);
        //try to reconnect after 5 seconds
        setWebSocketReconnectTimer((prevTimer) => {
          if(prevTimer) {
            clearInterval(prevTimer);
          }

          let timer = setInterval(() => {
            setupWebSocket();
          }, 5000);

          return timer;
        })
      }
    })

    webSocketRef.current.addEventListener('message', (e) => {
      const response = JSON.parse(e.data);
      if(response.type === 'game-data') {
        setGameData(response.gameData);
      } else if(response.type === 'server-timestamp') {
        setCurrentServerTimestamp(response.currentServerTimestamp);
      }
    })

    

    
  }

  function closeWebSocket() {
    //send unsubscribe req 
    if(webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      webSocketRef.current.send(JSON.stringify({
        type: 'unsubscribe-for-game-data',
        gameId,
        userId,
        userToken,
        sourceId: 'playGame'+wsRandomId,
      }))

      webSocketRef.current.send(JSON.stringify({
        type: 'unsubscribe-for-server-timestamp',
        userId,
        userToken,
        sourceId: 'timestampRequest'+wsRandomId,
      }))
    }

    //then close connection
    if(webSocketRef.current) {
      webSocketRef.current.close();
    }

    //clear  reconnect timer
    if(webSocketReconnectTimer) {
      clearInterval(webSocketReconnectTimer);
    }
  }

  function tellError(error){
    toast.error(error, {
      style: {
        borderRadius: '10px',
        background: 'var(--core-faintBlack)',
        color: 'var(--core-whitish)',
      },
    });
  }

  function tellMessage(message){
    toast.success(message, {
      style: {
        borderRadius: '10px',
        background: 'var(--core-faintBlack)',
        color: 'var(--core-whitish)',
      },
    });
  }

  function getBoard(_gameType) {
    if(_gameType === GAME_TYPES.BRAZILIAN_CHECKERS) {
      return <BrazilianCheckersBoard/>
    } else if(_gameType === GAME_TYPES.SPANISH_CHECKERS) {
      return <SpanishCheckersBoard/>
    } else if(_gameType === GAME_TYPES.ARUBASTINI) {
      return <ArubastiniBoard/>
    } else if(_gameType === GAME_TYPES.LASTCARD) {
      return <LastcardBoard/>
    }
  }

  async function getUserData() {
    let _data = null;
    await callApi("get-user-data", { userId, playMode }).then((response) => {
      if(response.status === 1) {
        _data = response.data;
        setUserData(response.data);
      } else {
        tellError(response.msg);
      }
    })

    return _data;
  }
  
  async function getGameData() {
    let _data = null;
    await callApi("get-game-data", { gameId, playMode }).then((response) => {
      if(response.status === 1) {
        _data = response.data;
        setGameData(response.data);
      } else {
        tellError(response.msg);
      }
    })

    return _data;
  }

  function triggerChat() {
    setShowChat(true);
    //make request to generate chatroom if not yet generated
  }

  async function sendDrawSignal() {
    //this sends the draw signal 
    tellMessage("Sending draw signal......");
    await callApi("send-draw-signal", { gameId, playMode, userId, userToken }).then((response) => {
      if(response.status === 1) {
        getGameData();
        tellMessage("Draw signal was sent");
      } else {
        tellError(response.msg);
      }
    })
  }

  async function ignoreDrawSignal() {
    await callApi("ignore-draw-signal", { gameId, playMode, userId, userToken }).then((response) => {
      if(response.status === 1) {
        getGameData()
        tellMessage("Draw signal was ignored");
      } else {
        tellError(response.msg);
      }
    })
  }

  function navBack() {
    mainApp.navBack();
  }

  function navHome() {
    mainApp.navHome();
  }

  function refresh() {
    mainApp.refresh();
  }

  async function acceptDrawSignal() {
    await callApi("accept-draw-signal", { gameId, playMode, userId, userToken }).then((response) => {
      if(response.status === 1) {
        getGameData()
        tellMessage("Draw signal was accepted");
      } else {
        tellError(response.msg);
      }
    })
  }

  async function surrenderGame() {
    if(Number(gameData.currentPlayer) === Number(userId)) {
      await callApi("surrender-game", { gameId, playMode, userId, userToken }).then((response) => {
        if(response.status === 1) {
          getGameData()
          tellMessage("You surrendered!");
        } else {
          tellError(response.msg);
        }
      })
    } else {
      tellError("Please wait for your turn to play if you want to surrender");
    }
  }

  async function checkForChat() {
    await callBaseApi("check_chat_for_game.php", { gameId }).then(response => {
      if(response.status === 1) {
        setChatRoomId(response.chatRoomId);
        return response.chatRoomId;
      }
    })
  }

  async function sendChatMessage() {
    //send game chat message
    if(chatMessage.trim().length > 0) {
      const message = {
        message: chatMessage,
        senderId: Number(userId)
      }

      //call api to send message
      await callApi("send-game-chat-message", { gameId, userId, userToken, message: JSON.stringify(message) }).then((response) => {
        if(response.status === 1) {
          setChatMessage('');
          getGameData();
        } else {
          tellError(response.msg);
        }
      })
    } else {
      tellError("Invalid message")
    }
  }

  async function sendQuickMessage(quickMessage) {
    //send game chat message
    if(quickMessage && quickMessage.trim().length > 0) {
      const message = {
        message: quickMessage,
        senderId: Number(userId)
      }

      //call api to send message
      await callApi("send-game-chat-message", { gameId, userId, userToken, message: JSON.stringify(message) }).then((response) => {
        if(response.status === 1) {
          getGameData();
        } else {
          tellError(response.msg);
        }
      })
    } else {
      tellError("Invalid message")
    }
  }
  
  useEffect(() => {
    setGameType(props.gameType);
  }, [ props.gameType ])

  useEffect(() => {
    setGameId(props.gameId);
  }, [ props.gameId ])

  useEffect(() => {
    setPlayMode(props.playMode);
  }, [ props.playMode ])

  useEffect(() => {
    setUserId(props.userId);
  }, [ props.userId ])

  useEffect(() => {
    setUserToken(props.userToken);
  }, [ props.userToken ])

  useEffect(() => {
    setMode(props.mode);
  }, [ props.mode ])

  useEffect(() => {
    if(webSocketConnected === 1) {
      tellMessage('Connected successfully to the Game server')

      //clear interval
      if(webSocketReconnectTimer) {
        clearInterval(webSocketReconnectTimer);
      }
    } else if(webSocketConnected === 0) {
      tellError("Could not connect to the Game Server, trying to re-connect in 5 seconds....")
    }
  }, [ webSocketConnected ])

  useEffect(() => {
    init();

    return () => {
      closeWebSocket();
    }
  }, [ gameType, gameId, playMode, userId, userToken ])

  const appContext = {
    gameId,
    gameType, 
    playMode,
    userId,
    userToken,
    userData,
    gameData,
    getGameData,
    sendDrawSignal,
    ignoreDrawSignal,
    acceptDrawSignal,
    surrenderGame,
    mode,
    showChat,
    setShowChat,
    checkForChat,
    chatRoomId,
    triggerChat,
    navBack,
    navHome,
    webSocketConnected,
    refresh,
    chatMessage,
    setChatMessage,
    sendChatMessage,
    sendQuickMessage,
    currentServerTimestamp,
  }

  
  if(ready) {
    return (
      <AppContext.Provider value={appContext}>
        
        <CorePaper/>
        <CoreChatter/>
        <Toaster
          position="bottom-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: "calc(1 + var(--core-superUltraHighIndex))"
          }}
        />
        <div className="CoreApp">
          {getBoard(gameType)}
        </div>
      </AppContext.Provider>
    )
  } else {
    return (
      <AppContext.Provider value={appContext}>
        <Toaster
          position="bottom-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: "calc(1 + var(--core-superUltraHighIndex))"
          }}
        />
        <div className="CoreApp">
          {getInlineLoader()}
        </div>
      </AppContext.Provider>
    )
  }
}